import { useQuery } from '@tanstack/react-query'

import { api } from 'services'

/**
 * VRG-Pricing.Access.Gate — a template's REAL subject, read from the registry.
 *
 * ## Why this exists
 *
 * `EmailPreview` renders a template's real bytes through
 * `GET /internal/labs/emails/:id/preview`, so an edit to the BODY lands on every
 * surface at once. That endpoint returns `text/html` and nothing else — no
 * subject — so a gate that wants to show "Subject: …" above the frame had no
 * choice but to type the string itself. The /internal/signups grant modal did,
 * and it drifted: the modal previewed "Your Vera pricing" while Mailpit received
 * "You have been granted access to view Vera pricing", with a comment directly
 * above claiming there was "no second copy to keep in step". The body half of
 * that claim was true. The subject half was the copy.
 *
 * So this closes the other half. `GET /internal/labs/emails` returns the whole
 * registry index, `subject` included (`listEmailTemplates` in
 * `packages/emails/src/render.ts`), and that registry subject is CANONICAL:
 * `sendPricingAccessLink` reads the same entry via `getEmailSubject`, which is
 * what makes "the subject shown is the subject sent" a property of the code
 * rather than of somebody's memory. `pricing-access-subject.guard.spec.ts`
 * asserts the send path and the registry cannot disagree.
 *
 * ## Why the index endpoint rather than a new one
 *
 * It already exists, it is already `@RequireAdmin({roles: [PlatformAdmin]})`
 * like the preview endpoint beside it, and both Labs sections already read it —
 * so there is no new API surface, and react-query's cache means a modal opening
 * repeatedly costs one request. A dedicated `/subject` route would be a second
 * endpoint returning a field this one already returns.
 *
 * Deliberately NOT a response header on the preview endpoint, which was the
 * other obvious shape: the platform and the API are different origins in dev, so
 * a custom header is invisible to JS unless `Access-Control-Expose-Headers`
 * lists it — a config dependency that fails silently and looks like a missing
 * subject rather than a missing header.
 *
 * ## Returns `undefined` while loading, and callers must not paper over it
 *
 * There is no fallback string here on purpose. A hardcoded default is the exact
 * defect this hook removes, and a stale guess rendered confidently is worse than
 * a subject that appears a beat late — the whole point of the line is that the
 * operator can trust it. Callers render a placeholder, never a literal.
 */

/** One row of `GET /internal/labs/emails`. Only the fields this hook needs. */
type EmailTemplateIndexEntry = {
  id: string
  subject?: string
}

const STALE_TIME_MS = 1000 * 60 * 60

export const useEmailTemplateSubject = (templateId: string): { subject?: string; isLoading: boolean } => {
  const { data, isLoading } = useQuery({
    // Keyed on the INDEX, not on the template, so every gate and every Labs
    // surface in a session shares one response rather than refetching the same
    // registry once per modal.
    queryKey: ['labsEmailTemplates'],
    queryFn: async () => {
      const { data: body } = await api.get('/internal/labs/emails')
      // `{templates: [...]}`, not a bare array — an `Array.isArray` guard here
      // is what left `EmailStructureLab`'s picker silently empty once before.
      return (body?.templates ?? []) as EmailTemplateIndexEntry[]
    },
    staleTime: STALE_TIME_MS,
  })

  return {
    subject: data?.find((t) => t.id === templateId)?.subject,
    isLoading,
  }
}
